import { SolanaWithoutSignerBaseCommand } from '../../../../solana';
import { chalkString } from '../../../../utils';

import { BN, web3 } from '@coral-xyz/anchor';
import { Args, Flags } from '@oclif/core';
import { getAccount, getAssociatedTokenAddressSync } from '@solana/spl-token';
import * as spl from '@solana/spl-token';
import { toUtf8 } from '@switchboard-xyz/common';
import {
  Oracle,
  Queue,
  QueueAccountData,
  State,
} from '@switchboard-xyz/on-demand';

function formatTokenToFixed(lamports: BN | bigint, decimals: number): string {
  const lamportStr = lamports.toString().padStart(decimals + 1, '0');
  const len = lamportStr.length;
  const integerPart = lamportStr.slice(0, len - decimals) || '0';
  const decimalPart = lamportStr.slice(len - decimals).padEnd(decimals, '0');
  return `${integerPart}.${decimalPart}`;
}

async function getAssociatedTokenBalance(
  connection: web3.Connection,
  mint: web3.PublicKey,
  owner: web3.PublicKey
) {
  const address = getAssociatedTokenAddressSync(mint, owner, true);
  const tokenAccount = await getAccount(connection, address);
  return tokenAccount.amount;
}

export default class QueuePrint extends SolanaWithoutSignerBaseCommand {
  static enableJsonFlag = true;

  static description = 'print a queue account';

  static flags = {
    ...SolanaWithoutSignerBaseCommand.flags,
    oracles: Flags.boolean({
      description: 'print oracles currently registered on this queue',
    }),
  };

  static args = {
    queue: Args.string({
      description: 'public key of the queue account',
      required: true,
    }),
  };

  async run() {
    const { args } = await this.parse(this.constructor as typeof QueuePrint);

    const program = await this.program;

    const queueAccount = new Queue(program, new web3.PublicKey(args.queue));
    const queue = await queueAccount.loadData();
    console.log(await this.prettyPrintQueue(queue, queueAccount.pubkey));
  }

  async prettyPrintQueue(
    queue: QueueAccountData,
    publicKey: web3.PublicKey,
    SPACING = 36
  ): Promise<string> {
    const program = await this.program;
    const state = await new State(program).loadData();

    const output: string[] = [];

    output.push('', chalkString('## Queue Data', '', SPACING));
    output.push(chalkString('publicKey', publicKey, SPACING));
    output.push(chalkString('authority', queue.authority, SPACING));

    // mrEnclaves
    output.push(
      '',
      chalkString('mrEnclavesLen', queue.mrEnclavesLen.toString(), SPACING)
    );
    output.push(chalkString('mrEnclaves', '', SPACING));
    for (let i = 0; i < queue.mrEnclavesLen; i++) {
      const mrEnclave = `0x${Buffer.from(queue.mrEnclaves[i]).toString('hex')}`;
      output.push(chalkString(`  mrEnclave-${i}`, mrEnclave, SPACING));
    }

    // const address = await spl.getAssociatedTokenAddress(
    //   spl.NATIVE_MINT,
    //   publicKey,
    //   true
    // );
    const connection = program.provider.connection;
    const queueSolAmount = await getAssociatedTokenBalance(
      connection,
      spl.NATIVE_MINT,
      publicKey
    );
    // const queueSolAta = await getAta(connection, WSOL_MINT, publicKey);
    output.push(
      chalkString(
        'queueSolBalance',
        formatTokenToFixed(queueSolAmount, 9),
        SPACING
      )
    );
    // output.push(
    // chalkString(
    // 'queueSolBalance',
    // formatTokenToFixed("0", 9),
    // SPACING
    // )
    // );

    // oracleKeys
    // output.push(
    // '',
    // chalkString('oracleKeysLen', queue.oracleKeysLen.toString(), SPACING)
    // );
    // output.push(chalkString('oracleKeys', '', SPACING));
    for (let i = 0; i < queue.oracleKeysLen; i++) {
      const oracle = new Oracle(program, queue.oracleKeys[i]);
      const oracleData = await oracle.loadData();

      output.push(chalkString(`    oracle-${i}`, queue.oracleKeys[i], SPACING));
      output.push(
        chalkString('        uri', toUtf8(oracleData.gatewayUri), SPACING)
      );
      output.push(
        chalkString('        operator', oracleData.operator, SPACING)
      );

      if (!oracleData.operator.equals(web3.PublicKey.default)) {
        try {
          const operatorSwitchBalance = await getAssociatedTokenBalance(
            this.connection,
            state.switchMint,
            oracleData.operator
          );
          output.push(
            chalkString(
              '      operatorSwitchBalance',
              formatTokenToFixed(operatorSwitchBalance, 9),
              SPACING
            )
          );
        } catch {
          this.log(
            `Failed to get operator switch balance for ${oracleData.operator.toString()}`
          );
        }
      }
    }

    output.push('');
    output.push(
      chalkString(
        'maxQuoteVerificationAge',
        queue.maxQuoteVerificationAge.toString(),
        SPACING
      )
    );
    output.push(
      chalkString(
        'lastHeartbeat',
        new Date(queue.lastHeartbeat.muln(1000).toNumber()).toISOString(),
        SPACING
      )
    );
    output.push(
      chalkString('nodeTimeout', queue.nodeTimeout.toString(), SPACING)
    );
    output.push(
      chalkString('oracleMinStake', queue.oracleMinStake.toString(), SPACING)
    );
    output.push(
      chalkString(
        'allowAuthorityOverrideAfter',
        queue.allowAuthorityOverrideAfter,
        SPACING
      )
    );
    output.push(chalkString('reward', queue.reward.toString(), SPACING));
    output.push(chalkString('currIdx', queue.currIdx.toString(), SPACING));
    output.push(chalkString('gcIdx', queue.gcIdx.toString(), SPACING));
    output.push(
      chalkString(
        'requireAuthorityHeartbeatPermission',
        queue.requireAuthorityHeartbeatPermission.toString(),
        SPACING
      )
    );
    output.push(
      chalkString(
        'requireAuthorityVerifyPermission',
        queue.requireAuthorityVerifyPermission.toString(),
        SPACING
      )
    );
    output.push(
      chalkString(
        'requireUsagePermissions',
        queue.requireUsagePermissions.toString(),
        SPACING
      )
    );
    output.push(chalkString('mint', queue.mint, SPACING));

    // Additional fields can be added here as needed

    return output.join('\n');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async catch(error: any) {
    super.catch(error, 'failed to print queue');
  }
}
